import { useLocale } from './LocaleProvider';

/**
 * Locale-aware absolute-date formatting. Companion to <c>useRelativeTime</c> for the cases
 * where you want a fixed date string ("13 May 2026") rather than a relative one ("3 days
 * ago"). The active platform locale (en, pt-br, fr, ko-kr etc.) is normalized to a BCP-47
 * tag (pt-br → pt-BR) before being passed to <c>Intl.DateTimeFormat</c>, so date strings
 * always come back in the user's language.
 *
 * <para>Usage: <c>const fmt = useDateFormat(); fmt(iso)</c> → "3 May 2026" (en) / "3 mai
 * 2026" (fr) / "2026年5月3日" (ja). Pass an <c>Intl.DateTimeFormatOptions</c> to override
 * per-call (e.g. <c>{ month: 'long' }</c>).</para>
 */
export function useDateFormat() {
    const { locale } = useLocale();
    const tag = intlLocaleTag(locale);

    return function fmt(when: Date | string | number, options?: Intl.DateTimeFormatOptions): string {
        const date =
            typeof when === 'number'
                ? new Date(when)
                : when instanceof Date
                  ? when
                  : new Date(when);
        if (Number.isNaN(date.getTime())) return '';
        try {
            return new Intl.DateTimeFormat(tag, options ?? DEFAULT_OPTIONS).format(date);
        } catch {
            // Pathological: unknown locale tag or unsupported options. Fall back to the
            // browser default.
            return date.toLocaleDateString(undefined, options ?? DEFAULT_OPTIONS);
        }
    };
}

const DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
};

// Manifest codes are platform-internal (e.g. "pt-br", "ko-kr"). Intl expects BCP-47 tags
// with the language subtag lowercase and the region subtag uppercase ("pt-BR", "ko-KR").
// Match the normalizer used by useRelativeTime so both helpers see the same canonical tag.
function intlLocaleTag(code: string): string {
    const [lang, region] = code.split('-');
    return region ? `${lang.toLowerCase()}-${region.toUpperCase()}` : lang.toLowerCase();
}
